import i18next from 'i18next'
import { CustomReportCategory } from '../../../../../redux/context/customReports/typesCategory'
import { ReportDataType } from '../../../../../redux/context/reports/types'
import { parseDate } from '../../../../../config/dayjsConfig'
import { defaultTitle, keyFigureTrans, formulaTrans } from '../../../../../redux/context/customReports/transUtils'

export type SegmentType =
  | 'company'
  | 'dimension'
  | 'period'
  | 'dataType'
  | 'function'
  | 'keyFigure'
  | 'formula'
  | 'reference'
  | 'custom'

export interface TitleSegment {
  text: string
  type: SegmentType
}

interface PlaceholderDef {
  type: SegmentType
  resolve: (category: CustomReportCategory) => string
}

const datePlaceholders: Record<string, (d: string) => string> = {
  '{yyyy}': d => parseDate(d)?.format('YYYY') || '',
  '{mm/yyyy}': d => parseDate(d)?.format('MM/YYYY') || '',
  '{mm/yy}': d => parseDate(d)?.format('MM/YY') || '',
  '{dd/mm/yyyy}': d => parseDate(d)?.format('DD/MM/YYYY') || '',
  '{dd}': d => parseDate(d)?.format('dd') || '',
  '{dddd}': d => parseDate(d)?.format('dddd') || '',
  '{w}': d => parseDate(d)?.format('w') || '',
  '{w/yyyy}': d => {
    const parsed = parseDate(d)
    return parsed ? `W${parsed.format('w')}/${parsed.format('YYYY')}` : ''
  }
}

const otherPlaceholders: Record<string, PlaceholderDef> = {
  '{company}': { type: 'company', resolve: c => c.company?.name || '' },
  '{dimensionName}': { type: 'dimension', resolve: c => c.dimension?.name || '' },
  '{dataType}': {
    type: 'dataType',
    resolve: c => i18next.t(`global:${c.dataType || ReportDataType.actuals}`)
  },
  '{function}': {
    type: 'function',
    resolve: c => i18next.t(`functionCategory:${c.value}`)
  },
  '{keyFigure}': { type: 'keyFigure', resolve: c => keyFigureTrans(c) },
  '{formula}': { type: 'formula', resolve: c => formulaTrans(c) },
  '{scenarioName}': { type: 'custom', resolve: c => c.budgetingScenario?.name || '' }
}

const PLACEHOLDER_REGEX = /\{[^}]+\}/g
const DATE_FIELDS: ('startDate' | 'endDate')[] = ['startDate', 'endDate']

function resolveTemplate(template: string, category: CustomReportCategory): TitleSegment[] {
  const segments: TitleSegment[] = []
  let dateFieldIndex = 0

  for (const line of template.split('\n')) {
    const trimmed = line.trim()
    if (trimmed) {
      let remaining = trimmed
      const lineParts: TitleSegment[] = []

      let match = PLACEHOLDER_REGEX.exec(remaining)
      while (match) {
        const before = remaining.slice(0, match.index).trim()
        const placeholder = match[0]
        remaining = remaining.slice(match.index + placeholder.length)
        PLACEHOLDER_REGEX.lastIndex = 0

        if (before) lineParts.push({ text: before, type: 'custom' })

        const otherDef = otherPlaceholders[placeholder]
        if (otherDef) {
          const text = otherDef.resolve(category)
          if (text) {
            lineParts.push({ text, type: otherDef.type })
          }
        } else if (datePlaceholders[placeholder]) {
          const field = DATE_FIELDS[dateFieldIndex]
          dateFieldIndex += 1
          const dateValue = field ? category[field] : undefined
          if (dateValue) {
            const text = datePlaceholders[placeholder](dateValue)
            if (text) lineParts.push({ text, type: 'period' })
          }
        }

        match = PLACEHOLDER_REGEX.exec(remaining)
      }

      const afterTrimmed = remaining.trim()
      if (afterTrimmed) lineParts.push({ text: afterTrimmed, type: 'custom' })

      if (lineParts.length === 1) {
        segments.push(lineParts[0])
      } else if (lineParts.length > 1) {
        segments.push({
          text: lineParts.map(p => p.text).join(' '),
          type: lineParts[0].type
        })
      }
    }
  }

  return segments
}

const hasPlaceholders = (title: string): boolean => /\{[^}]+\}/.test(title)

export const getCategoryOwnSegments = (category: CustomReportCategory): TitleSegment[] => {
  const title = category.title ?? defaultTitle(category.type, category.value)
  const trimmed = title.trim()
  if (!trimmed) return []
  if (hasPlaceholders(trimmed)) return resolveTemplate(title, category)
  return [{ text: trimmed, type: 'custom' }]
}

const findCategoryById = (id: string, categories: CustomReportCategory[]): CustomReportCategory | null => {
  for (const cat of categories) {
    if (cat.id === id) return cat
    if (cat.children) {
      const found = findCategoryById(id, cat.children)
      if (found) return found
    }
  }
  return null
}

function visitCategory(
  category: CustomReportCategory,
  allCategories: CustomReportCategory[],
  visited: Set<string> = new Set()
): TitleSegment[] {
  if (category.type === 'reference') {
    const refId = category.value as string
    if (!refId || visited.has(refId)) return []
    visited.add(refId)
    const target = findCategoryById(refId, allCategories)
    if (!target) return []
    const result = visitCategory(target, allCategories, visited)
    visited.delete(refId)
    return result
  }

  const segments: TitleSegment[] = [...getCategoryOwnSegments(category)]

  if (category.children) {
    for (const child of category.children) {
      segments.push(...visitCategory(child, allCategories, visited))
    }
  }

  return segments
}

export const getCategoryTitleSegments = (
  category: CustomReportCategory,
  allCategories: CustomReportCategory[] = []
): TitleSegment[] => {
  return visitCategory(category, allCategories).filter(s => s.text)
}

export type AncestorSegmentsMap = Map<string, TitleSegment[]>

function collectAncestorSegments(
  categories: CustomReportCategory[],
  inheritedSegments: TitleSegment[],
  map: AncestorSegmentsMap
): void {
  for (const cat of categories) {
    if (cat.id) map.set(cat.id, inheritedSegments)
    if (cat.children) {
      collectAncestorSegments(cat.children, [...inheritedSegments, ...getCategoryOwnSegments(cat)], map)
    }
  }
}

// Single top-down pass building, for every node id, the segments of its ancestors (e.g.
// company/dimension nodes above a periodGroup/function leaf) — getCategoryTitleSegments only
// walks downward from a given node, so this covers the leaf's missing breadcrumb in one lookup
// instead of re-searching the tree per leaf.
export const buildAncestorSegmentsMap = (categories: CustomReportCategory[]): AncestorSegmentsMap => {
  const map: AncestorSegmentsMap = new Map()
  collectAncestorSegments(categories, [], map)
  return map
}

export const getAncestorSegments = (
  category: CustomReportCategory,
  ancestorMap: AncestorSegmentsMap
): TitleSegment[] => {
  const targetId = category.type === 'reference' ? (category.value as string) : category.id
  if (!targetId) return []
  return (ancestorMap.get(targetId) ?? []).filter(s => s.text)
}

export const getCategoryOwnTitle = (category: CustomReportCategory): string => {
  return getCategoryOwnSegments(category)
    .map(s => s.text)
    .join('\n')
    .trim()
}

export const getCategoryPlainTitle = (
  category: CustomReportCategory,
  allCategories: CustomReportCategory[] = []
): string => {
  return getCategoryTitleSegments(category, allCategories)
    .map(s => s.text)
    .join('\n')
    .trim()
}

type SegmentStyleMap = Partial<Record<SegmentType, string>>

export const segmentsToHtml = (segments: TitleSegment[], styleMap?: SegmentStyleMap): string => {
  return segments
    .map(s => {
      const typeStyle = styleMap?.[s.type] || ''
      return `<span style="display:block;${typeStyle}">${s.text}</span>`
    })
    .join('')
}

export const MAX_SEGMENTS = 6
const SEGMENT_LINE_HEIGHT = 10
const HEADER_PADDING = 10
const MIN_HEADER_HEIGHT = 24
export const GROUP_HEADER_HEIGHT = 24
const GROUP_HEADER_HEIGHT_LONG_NAME = 38
const GROUP_NAME_LONG_THRESHOLD = 24

function hasLongGroupName(categories: CustomReportCategory[]): boolean {
  return categories.some(
    cat =>
      !!cat.children?.length &&
      (getCategoryOwnTitle(cat).length > GROUP_NAME_LONG_THRESHOLD || hasLongGroupName(cat.children))
  )
}

// groupHeaderHeight is a single grid-wide value (ag-Grid can't size individual group
// header cells), so this just bumps it a bit when any dimension/group name is long enough
// to need a second line, instead of measuring exact wrap widths.
export const computeGroupHeaderHeight = (categories: CustomReportCategory[]): number =>
  hasLongGroupName(categories) ? GROUP_HEADER_HEIGHT_LONG_NAME : GROUP_HEADER_HEIGHT

function findDeepestLeafSegmentCount(
  categories: CustomReportCategory[],
  depth: number
): { maxDepth: number; maxSegments: number } {
  let result = { maxDepth: -1, maxSegments: 0 }
  for (const cat of categories) {
    if (cat.type === 'function' || !cat.children?.length) {
      if (depth > result.maxDepth) {
        result = { maxDepth: depth, maxSegments: getCategoryTitleSegments(cat, categories).length }
      } else if (depth === result.maxDepth) {
        result.maxSegments = Math.max(result.maxSegments, getCategoryTitleSegments(cat, categories).length)
      }
    } else {
      const child = findDeepestLeafSegmentCount(cat.children, depth + 1)
      if (child.maxDepth > result.maxDepth) {
        result = child
      } else if (child.maxDepth === result.maxDepth) {
        result.maxSegments = Math.max(result.maxSegments, child.maxSegments)
      }
    }
  }
  return result
}

export const computeHeaderHeight = (categories: CustomReportCategory[]): number => {
  const { maxSegments } = findDeepestLeafSegmentCount(categories, 0)
  const leafSegments = Math.min(maxSegments, MAX_SEGMENTS)
  return Math.max(MIN_HEADER_HEIGHT, leafSegments * SEGMENT_LINE_HEIGHT + HEADER_PADDING)
}
